import { useMemo, useState } from "react";

import {
  AutoFillStatus,
  IntakeForm,
  IntakeNotFound,
  SectionItem,
  Toast,
  VoiceConversation,
} from "@smart/bridge-intake-components-dom";
import {
  appendDownloadFormLink,
  defaultTemplate,
} from "@smart/bridge-templates-basic";
import { SubmissionNS } from "@smart/bridge-types-basic";
import { Loading } from "@smart/itops-sb-design-system-dom";
import { useLoadStaffDetails } from "@smart/manage-gql-client-dom";
import {
  useFormForDisplay,
  useLoadFormForDisplay,
} from "@smart/manage-hooks-dom";

import { manageVoiceWebsocket } from "./voice-websocket";
import {
  GqlField,
  GqlForm,
  GqlGroup,
  GqlSection,
  GqlSetting,
  GqlTeam,
} from "../types";

type PreviewProps = {
  formUri: string;
  team?: GqlTeam | null;
  setting?: GqlSetting | null;
  form?: GqlForm | null;
  sections?: GqlSection[] | null;
  groups?: GqlGroup[] | null;
  fields?: GqlField[] | null;
  canUpdate: boolean;
  previewOptions: {
    previewErrorToggle: [
      boolean,
      React.Dispatch<React.SetStateAction<boolean>>,
    ];
    previewResponses: [
      Record<string, any>,
      React.Dispatch<React.SetStateAction<Record<string, any>>>,
    ];
    previewLastSection: [
      SectionItem | "summary" | undefined,
      React.Dispatch<React.SetStateAction<SectionItem | "summary" | undefined>>,
    ];
  };
  previewLoading?: boolean;
  staffDetails: ReturnType<typeof useLoadStaffDetails>;
  intakeFormKey?: string;
  setToasts?: (toasts: Toast[]) => void;
};

const Preview = ({
  intakeFormKey,
  team,
  setting,
  form,
  sections,
  groups,
  fields,
  canUpdate,
  formUri,
  previewOptions,
  previewLoading,
  staffDetails,
  setToasts,
}: PreviewProps) => {
  const draftDisplay = useFormForDisplay({
    loading: previewLoading || false,
    team,
    setting,
    form: form?.rawForm,
    sections,
    groups,
    fields,
  });
  // Read-only users preview the published version, editors keep the draft
  const { rawSections, rawGroups, rawFields, ...publishedDisplay } =
    useLoadFormForDisplay({ formUri: canUpdate ? undefined : formUri });

  const { loading, exists, ...rest } = canUpdate
    ? draftDisplay
    : publishedDisplay;

  // Published preview needs staff for its own fields, not the draft's.
  const publishedStaffDetails = useLoadStaffDetails({
    staffIds: [
      ...new Set(
        rawFields?.flatMap((field) => field.values.availableStaffIds ?? []),
      ),
    ],
  });
  const effectiveStaffDetails = canUpdate
    ? staffDetails
    : publishedStaffDetails;

  const [autoFillStatus, setAutoFillStatus] = useState<AutoFillStatus>();
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const submissionUri = useMemo(() => SubmissionNS.generateUri(), []);

  if (loading || effectiveStaffDetails.loading) return <Loading />;

  if (!canUpdate && !exists) return <IntakeNotFound />;

  return (
    <div className="relative h-full w-full">
      <IntakeForm
        key={intakeFormKey}
        intakeFormKey={intakeFormKey}
        {...rest}
        form={{
          uri: form?.uri || "",
          ...rest.form,
          response: appendDownloadFormLink(
            rest.form.response || defaultTemplate.response,
          ),
        }}
        submission={{ uri: submissionUri, responses: {} }}
        submitResponses={async ({ responses }) => {
          previewOptions.previewResponses[1]((prev) => ({
            ...prev,
            ...responses,
          }));
          return {
            operationIds: ["mockId"],
            submissionStatus: {},
          };
        }}
        previewOptions={previewOptions}
        isPreview
        showPaymentPlaceholder
        loadResponses={async () => undefined}
        staffDetails={effectiveStaffDetails.result || []}
        runAutoFilling={async () => {
          setAutoFillStatus("filling");
          setTimeout(() => setAutoFillStatus("completed"), 3000);
        }}
        autoFillStatus={autoFillStatus}
        submitAutofillFeedback={async () => setAutoFillStatus(undefined)}
        setToasts={setToasts}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        voiceWebSocket={manageVoiceWebsocket}
        VoiceConversationComponent={
          setting?.voiceGuide?.enabled ? VoiceConversation : undefined
        }
      />
    </div>
  );
};

export { Preview };
